import Button from '@mui/material/Button';
import React from 'react';

interface ImageUploaderProps {
  onImageResized: (file: File) => void;
  isUploading?: boolean;
}

const resizeImage = (file: File, width: number, height: number): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        // Calculate aspect ratio fit
        const ratio = Math.min(width / img.width, height / img.height);
        const newWidth = Math.round(img.width * ratio);
        const newHeight = Math.round(img.height * ratio);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas context not available'));

        // Ensure transparent background
        ctx.clearRect(0, 0, width, height);

        // Center the image
        const dx = Math.floor((width - newWidth) / 2);
        const dy = Math.floor((height - newHeight) / 2);

        ctx.drawImage(img, dx, dy, newWidth, newHeight);

        canvas.toBlob((blob) => {
          if (!blob) return reject(new Error('Canvas is empty'));
          const resizedFile = new File([blob], file.name, { type: file.type });
          resolve(resizedFile);
        }, file.type);
      };
      img.onerror = reject;
      img.src = e.target?.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const ImageUploader: React.FC<ImageUploaderProps> = ({ onImageResized, isUploading = false }) => {
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const resized = await resizeImage(file, 384, 384);
      onImageResized(resized);
    } catch (err) {
      alert('Failed to resize image');
    }
  };

  return (
    <Button
      component="label"
      size="small"
      variant="contained"
      sx={{ minWidth: 0, px: 1, fontSize: 12 }}
      disabled={isUploading}
    >
      {isUploading ? 'Uploading...' : 'Upload Image'}
      <input
        type="file"
        accept="image/*"
        hidden
        onChange={handleChange}
        disabled={isUploading}
      />
    </Button>
  );
};

export default ImageUploader;
